/**
 * PitchAnalysisDashboardPage - Advanced Scouting Hub
 *
 * Three modes:
 *   1. Game Film — PitchAnalysisDashboard with batch data (default)
 *   2. Movement Analysis — Upload/record video + Gemini structured analysis
 *   3. Live Analysis — Real-time AI via Gemini Multimodal Live API (WebSocket)
 */

import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Loader2, AlertTriangle, RefreshCw, Film, Camera, Radio } from 'lucide-react';
import PitchAnalysisDashboard from '../components/wire/PitchAnalysisDashboard';

const MovementAnalysisContent = lazy(() => import('../components/movement-analysis/MovementAnalysisContent'));
const LiveVideoTab = lazy(() => import('../components/video/LiveVideoTab'));

const MODES = [
  { id: 'game-film', label: 'Game Film', icon: Film, description: 'Video segment analysis' },
  { id: 'movement', label: 'Movement Analysis', icon: Camera, description: 'Upload or record for AI form analysis' },
  { id: 'live', label: 'Live Analysis', icon: Radio, description: 'Real-time AI via camera or screen' },
];

/**
 * PDF Export Handler
 */
async function handleExportPDF(viewType, data, selectedPitcher) {
  // Dynamic import to avoid loading jsPDF unless needed
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Load logo
  const logoImg = new Image();
  logoImg.crossOrigin = 'anonymous';
  logoImg.src = '/SLAM_logo.png';

  await new Promise((resolve) => {
    logoImg.onload = resolve;
    logoImg.onerror = () => resolve(); // Continue even if logo fails
  });

  // Header
  doc.setFillColor(30, 30, 35);
  doc.rect(0, 0, pageWidth, 45, 'F');

  // Add logo if loaded
  if (logoImg.complete && logoImg.naturalWidth > 0) {
    const logoHeight = 25;
    const logoWidth = (logoImg.naturalWidth / logoImg.naturalHeight) * logoHeight;
    doc.addImage(logoImg, 'PNG', 14, 5, logoWidth, logoHeight);
  }

  doc.setFontSize(18);
  doc.setTextColor(255, 212, 0);
  doc.text('SLAM Sports AI', 50, 18);

  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text(`Pitch Analysis Dashboard - ${viewType === 'coaches' ? 'Coaches View' : 'Hitters View'}`, 50, 28);
  doc.text(`THE WIRE Report`, 50, 36);

  // Summary stats
  let yPos = 55;

  doc.setFontSize(14);
  doc.setTextColor(30, 30, 35);
  doc.text('Analysis Summary', 14, yPos);
  yPos += 10;

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 105);

  const summary = data.summary;
  doc.text(`Total Videos: ${summary.totalVideos}`, 14, yPos); yPos += 6;
  doc.text(`Patterns Detected: ${summary.tippingDetected} (${(summary.overallTippingRate * 100).toFixed(1)}%)`, 14, yPos); yPos += 6;
  doc.text(`Batch ID: ${summary.batchId}`, 14, yPos); yPos += 6;
  doc.text(`Completed: ${new Date(summary.completedAt).toLocaleString()}`, 14, yPos); yPos += 15;

  // Pitcher breakdown
  if (selectedPitcher && data.byPitcher[selectedPitcher]) {
    const pitcher = data.byPitcher[selectedPitcher];

    doc.setFontSize(14);
    doc.setTextColor(30, 30, 35);
    doc.text(`Pitcher: ${selectedPitcher}`, 14, yPos); yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 105);
    doc.text(`Total Pitches: ${pitcher.totalPitches}`, 14, yPos); yPos += 6;
    doc.text(`Pattern Rate: ${(pitcher.patternRate * 100).toFixed(1)}%`, 14, yPos); yPos += 6;
    doc.text(`Avg Confidence: ${(pitcher.avgConfidence * 100).toFixed(1)}%`, 14, yPos); yPos += 15;

    // Top tells
    if (pitcher.topTellsArray && pitcher.topTellsArray.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(30, 30, 35);
      doc.text('Key Tells:', 14, yPos); yPos += 8;

      doc.setFontSize(9);
      pitcher.topTellsArray.slice(0, 10).forEach((tell, idx) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.setTextColor(100, 100, 105);
        doc.text(`${idx + 1}. ${tell.label} - ${tell.count} occurrences (${(tell.avgConfidence * 100).toFixed(0)}% avg)`, 14, yPos);
        yPos += 5;
      });
    }
  } else {
    // All pitchers summary
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 35);
    doc.text('Pitcher Summary', 14, yPos); yPos += 10;

    Object.entries(data.byPitcher).forEach(([pitcher, pData]) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(11);
      doc.setTextColor(30, 30, 35);
      doc.text(pitcher, 14, yPos); yPos += 6;

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 105);
      doc.text(`${pData.totalPitches} pitches | ${(pData.patternRate * 100).toFixed(1)}% tipping | ${pData.topTellsArray?.length || 0} tell types`, 20, yPos);
      yPos += 10;
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated by SLAM Sports AI | ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      290,
      { align: 'center' }
    );
  }

  // Save
  const filename = selectedPitcher
    ? `pitch-tipping-${selectedPitcher.toLowerCase()}-${viewType}.pdf`
    : `pitch-tipping-all-pitchers-${viewType}.pdf`;
  doc.save(filename);
}

/**
 * Main Page Component
 */
export default function PitchAnalysisDashboardPage() {
  const [mode, setMode] = useState('game-film');
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setBatchData(null);
    setLoading(false);
  }, []);

  // Refresh handler (for future API integration — no demo data)
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Future: fetch real analysis data from API
      setBatchData(null);
      setLoading(false);
    } catch (err) {
      setError('Failed to refresh data');
      setLoading(false);
    }
  }, []);

  const renderModeContent = () => {
    if (mode === 'movement') {
      return (
        <Suspense fallback={<div className="flex items-center justify-center h-64 text-gray-400"><Loader2 className="animate-spin mr-2" size={20} /> Loading...</div>}>
          <div className="min-h-full bg-[#040608] text-zinc-100 p-4 md:p-6">
            <div className="max-w-6xl mx-auto">
              <MovementAnalysisContent />
            </div>
          </div>
        </Suspense>
      );
    }

    if (mode === 'live') {
      return (
        <Suspense fallback={<div className="flex items-center justify-center h-64 text-gray-400"><Loader2 className="animate-spin mr-2" size={20} /> Loading...</div>}>
          <div className="flex-1" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <LiveVideoTab />
          </div>
        </Suspense>
      );
    }

    // Default: game-film
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-sky-400 animate-spin mx-auto" />
            <p className="text-gray-500 dark:text-slate-400 mt-4">Loading analysis data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
            <p className="text-gray-900 dark:text-white text-lg mt-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-sky-500 text-black
                rounded-lg hover:bg-sky-400 transition-colors mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <PitchAnalysisDashboard
        batchData={batchData}
        onExportPDF={handleExportPDF}
      />
    );
  };

  return (
    <div className="flex flex-col h-full bg-slate-950">
      {/* Mode selector bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-4 py-1.5 bg-gray-900/60 border-b border-gray-700/50">
        {MODES.map((m) => {
          const Icon = m.icon;
          const isActive = mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-sky-500/20 text-sky-400 border border-amber-500/30'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
              }`}
              title={m.description}
            >
              <Icon size={14} />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Mode content */}
      <div className="flex-1 overflow-auto">
        {renderModeContent()}
      </div>
    </div>
  );
}
